/**
 * Play — the in-match route.
 *
 * Pure projection of the server-authoritative GameState. The lobby store
 * brought up the WebSocket connection at /game/:sessionId/lobby; on
 * mount this route attaches the gameStore to the same client so the
 * authoritative GameState frames flow into Zustand.
 *
 * Layout (top → bottom):
 *   - StatusBar           round/turn-phase/current-player chip
 *   - PhaseCoach          "what to do now" banner (per-phase + combat-aware)
 *   - MapView             ~60% of viewport; territories click-bound to
 *                         SELECT_SOURCE / SELECT_TARGET dispatchers
 *   - ProjectDashboard    ~30%; tier zones drive PLAY_DOCUMENTATION_CARD
 *                         dispatch when a card is dragged onto a tier
 *   - CardHand            self player's hand of doc cards
 *   - ChalkQuizModal      conditionally shown based on attackState.kind
 *   - GameOverOverlay     when outerPhase === 'ended'
 *
 * Phase-specific bottom-bar buttons live in `PhaseControls`:
 *   - draft-plan / document-deploy / execute-tests → END PHASE
 *   - incident-fortify                              → END TURN
 *   - territoryConquered (post-conquest)            → handled in modal
 *   - combatActive / combatResolved                 → modal owns flow
 *   - sourceSelected / targetSelected               → COMMIT_ATTACK trio
 */

import { useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DndContext } from '@dnd-kit/core';
import {
  asSessionId,
  type AttackState,
  type GameState,
  type PlayerId,
  type QuestionId,
  type SessionId,
} from '@qd/protocol';
import { buildActiveMap, TERRITORIES, type PlayerCount, type TerritoryId } from '@qd/map';
import { canPlayerAttack } from '@qd/rules';
import { fontStacks, surfaces } from '@qd/design-tokens';
import { SlateBackground } from '../components/base/SlateBackground';
import { ChalkButton } from '../components/base/ChalkButton';
import { ChalkQuizModal } from '../components/combat';
import { MapView } from '../components/map/MapView';
import {
  GameOverOverlay,
  HowToPlayModal,
  PhaseCoach,
  PHASE_COACH_HEIGHT,
  PhaseTransitionToast,
  StatusBar,
  PlayerRoster,
} from '../components/play';
import { MobilePlayLayout } from '../components/play/mobile';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  useActiveQuestion,
  useAttackState,
  useCurrentPlayer,
  useGameState,
  useGameStore,
  useLearningAnswer,
  useSelfDashboard,
  useSelfPlayer,
} from '../stores/gameStore';
import { useLobbyStore, useSessionSettings } from '../stores/lobbyStore';
import { useTurnTimer } from '../hooks/useTurnTimer';

export function Play() {
  const { sessionId: routeSessionId = '' } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const sessionId: SessionId = asSessionId(routeSessionId);

  const lobbySelfId = useLobbyStore((s) => s.selfId);
  const lobbySessionId = useLobbyStore((s) => s.sessionId);

  // Attach to the lobby store's WsClient. The lobby store is the entry
  // point — we presume it's already connected (the player walked from
  // /lobby → /play via beginMatch). If the user reloaded directly on
  // /play, also drive the lobby store to (re)connect against the same
  // session id so the WebSocket comes back up.
  const lobbyConnect = useLobbyStore((s) => s.connect);
  const attachGame = useGameStore((s) => s.attach);
  const detachGame = useGameStore((s) => s.detach);

  useEffect(() => {
    if (lobbySessionId !== sessionId) {
      lobbyConnect(sessionId);
    }
    attachGame();
    return () => {
      detachGame();
    };
  }, [sessionId, lobbySessionId, lobbyConnect, attachGame, detachGame]);

  const gameState = useGameState();
  const selfPlayer = useSelfPlayer(lobbySelfId);
  const dashboard = useSelfDashboard(lobbySelfId);
  const attackState = useAttackState();
  const currentPlayer = useCurrentPlayer();
  // Subscribe to active question so the modal re-renders on chain
  // attacks (the gameState object identity changes, but selecting the
  // question explicitly keeps the dependency graph honest for tooling).
  void useActiveQuestion();

  const playerCount = derivePlayerCount(gameState);

  // Action dispatchers from the gameStore.
  // CONTINUE_ATTACK is gone in the buzzer-race redesign — combat is
  // one question, no chain, so there's no continueAttack dispatcher.
  // endCombat stays because it still cancels a targetSelected attack
  // back to idle; the modal also fires it via onCombatResolvedDismiss
  // after a short reveal of the resolved frame.
  const selectSource = useGameStore((s) => s.selectSource);
  const selectTarget = useGameStore((s) => s.selectTarget);
  const commitAttack = useGameStore((s) => s.commitAttack);
  const answerQuestion = useGameStore((s) => s.answerQuestion);
  const endCombat = useGameStore((s) => s.endCombat);
  const moveCubesAfterConquest = useGameStore((s) => s.moveCubesAfterConquest);
  const endPhase = useGameStore((s) => s.endPhase);
  const endTurn = useGameStore((s) => s.endTurn);
  const submitLearningAnswer = useGameStore((s) => s.submitLearningAnswer);
  const turnTimeout = useGameStore((s) => s.turnTimeout);

  // Track the last in-flight question id across combatActive →
  // combatResolved. The combatResolved AttackState doesn't carry the
  // question id, but the server's learningAnswers is keyed by it, so we
  // need this so per-player feedback (rendered at combatResolved) can
  // look up the just-answered entry.
  const lastQuestionIdRef = useRef<QuestionId | null>(null);
  if (attackState.kind === 'combatActive') {
    lastQuestionIdRef.current = attackState.question.id as QuestionId;
  } else if (attackState.kind === 'idle') {
    // Combat fully wound down; clear the cache so the next chain attack
    // doesn't show stale feedback.
    lastQuestionIdRef.current = null;
  }
  const activeQuestionId =
    attackState.kind === 'combatActive'
      ? (attackState.question.id as QuestionId)
      : lastQuestionIdRef.current;
  const localLearningAnswer = useLearningAnswer(activeQuestionId, lobbySelfId);

  // Territory display name lookup for combat header.
  const territoryNameById = useMemo(() => {
    return buildTerritoryNameLookup(playerCount);
  }, [playerCount]);

  const sourceName = sourceTerritoryName(attackState, territoryNameById);
  const targetName = targetTerritoryName(attackState, territoryNameById);

  // (Drag-end handler removed in the simplified-play loop. Documentation
  // cards are auto-resolved by the autopilot, so neither layout exposes
  // a hand surface anymore.)

  const isMobile = useIsMobile();
  const myTurn = gameState ? isMyTurn(gameState, lobbySelfId) : false;

  // ─── Defender derivation (buzzer-race combat) ───
  // The defender is the player who currently controls the territory
  // under attack. The reducer now accepts ANSWER_QUESTION from EITHER
  // the attacker OR the defender; first-correct-wins. We compute this
  // from the territory runtime state so it stays correct across all
  // combat-modal kinds (combatActive / combatResolved / territoryConquered)
  // — `combatResolved.targetId` is the same territory id that was under
  // attack in `combatActive`, so the defender's identity is stable
  // throughout one combat.
  const isDefender = useMemo(() => {
    if (!lobbySelfId || !gameState) return false;
    if (
      attackState.kind !== 'combatActive' &&
      attackState.kind !== 'combatResolved' &&
      attackState.kind !== 'territoryConquered'
    ) {
      return false;
    }
    const target = gameState.territories[attackState.targetId];
    return target?.controllingPlayerId === lobbySelfId;
  }, [attackState, gameState, lobbySelfId]);

  // ─── Lockout flag derivation ───
  // BR-Backend's protocol changes add `attackerLockedOut` /
  // `defenderLockedOut` booleans onto AttackState['combatActive']. Until
  // those land in main, read defensively with a runtime cast: the field
  // simply doesn't exist on this branch yet, so the fallback `false`
  // preserves current behaviour. After merge the cast becomes a no-op.
  const attackerLockedOut =
    attackState.kind === 'combatActive'
      ? Boolean(
          (attackState as unknown as { attackerLockedOut?: boolean })
            .attackerLockedOut,
        )
      : false;
  const defenderLockedOut =
    attackState.kind === 'combatActive'
      ? Boolean(
          (attackState as unknown as { defenderLockedOut?: boolean })
            .defenderLockedOut,
        )
      : false;

  // Modal dispatch handlers. The attacker's onSubmit dispatches
  // ANSWER_QUESTION (resolves combat); non-attackers route through
  // SUBMIT_LEARNING_ANSWER (advisory only). TURN_TIMEOUT only fires
  // for the current turn-holder (the attacker) — server-side validates
  // that the configured timer has elapsed.
  const handleLearningSubmit = useCallback(
    (choiceIndex: number | boolean) => {
      if (!lobbySelfId || !activeQuestionId) return;
      submitLearningAnswer(lobbySelfId, activeQuestionId, choiceIndex);
    },
    [activeQuestionId, lobbySelfId, submitLearningAnswer],
  );

  // Stabilize handleTurnTimeout's identity. Reading lobbySelfId/myTurn
  // via a ref means this callback NEVER changes identity over a
  // component's lifetime — which matters because it is wired into the
  // CombatTimerArc's setInterval effect. Even with the timer arc's own
  // ref-based hardening, keeping this callback stable is belt-and-braces
  // — any consumer (now or later) that uses `handleTurnTimeout` as an
  // effect dependency stays correct.
  const turnTimeoutGuardRef = useRef({ lobbySelfId, myTurn });
  turnTimeoutGuardRef.current = { lobbySelfId, myTurn };
  const handleTurnTimeout = useCallback(() => {
    const { lobbySelfId: id, myTurn: mine } = turnTimeoutGuardRef.current;
    if (!id) return;
    if (!mine) return;
    turnTimeout(id);
  }, [turnTimeout]);

  // Active map is needed below for canPlayerAttack's adjacency check.
  const stuckMap = useMemo(
    () => buildActiveMap(playerCount),
    [playerCount],
  );

  // ───────────────────────────────────────────────────────────────────────
  // Rescue-deploy detection
  // ───────────────────────────────────────────────────────────────────────
  // Simplified play accumulates reserves without ever auto-deploying
  // (see the autopilot block below). A player can end up with every
  // owned territory at 1 cube — `validAttackTargets` requires source
  // cubeCount >= 2, so they cannot attack. Without rescue-deploy, the
  // only out is End Turn, which feels like a softlock.
  //
  // When the player CAN'T attack but HAS reserves, we surface a
  // rescue-deploy mode: tap an owned territory → DEPLOY_CUBES count=1.
  // The reducer's rescue-deploy branch (skip-tier-gate, requires
  // !canPlayerAttack) accepts this. The reserve cube turns a 1-cube
  // territory into a 2-cube one, unsticking the player.
  //
  // When the player has no reserves either, we surface a hard-stuck
  // message and prompt End Turn.
  const playerCanAttack =
    gameState && lobbySelfId
      ? canPlayerAttack(lobbySelfId, gameState, stuckMap)
      : false;

  const inRescueDeployMode =
    myTurn &&
    gameState?.phase === 'execute-tests' &&
    attackState.kind === 'idle' &&
    !playerCanAttack &&
    (selfPlayer?.reserveCubes ?? 0) > 0;

  const isHardStuck =
    myTurn &&
    gameState?.phase === 'execute-tests' &&
    attackState.kind === 'idle' &&
    !playerCanAttack &&
    (selfPlayer?.reserveCubes ?? 0) === 0;

  const deployCubes = useGameStore((s) => s.deployCubes);
  const onRescueDeploy = (territoryId: TerritoryId) => {
    deployCubes(String(territoryId), 1);
  };

  // Per-turn time limit from session settings (configured in Create.tsx).
  // 0 = disabled; otherwise the timer hook below counts down each turn.
  const sessionSettings = useSessionSettings();
  const turnTimerSeconds = sessionSettings?.turnTimer ?? 0;
  const currentPlayerIdx = gameState?.currentPlayerIdx ?? 0;
  const turnNumber = gameState?.turnNumber ?? 0;

  // Drive the same countdown the StatusBar TimerChip displays; we only
  // care about the `isExpired` edge here so we can auto-end the phase
  // for the current player.
  const turnTimer = useTurnTimer({
    durationSec: turnTimerSeconds,
    turnNumber,
    currentPlayerIdx,
  });

  // Guard: fire the timer-expired endPhase() exactly ONCE per (turn, player)
  // pair. Without this, `isExpired` stays `true` from when we call endPhase()
  // until the server broadcasts the new turn — every React re-render in that
  // window would re-fire the effect, sending repeated END_PHASE dispatches
  // that advance extra phases and cause reserve accumulation.
  const timerEndPhaseFiredRef = useRef(false);
  useEffect(() => {
    // Reset the guard whenever the turn or player index changes (new turn).
    timerEndPhaseFiredRef.current = false;
  }, [turnNumber, currentPlayerIdx]);

  // ───────────────────────────────────────────────────────────────────────
  // Simplified-play auto-pilot
  // ───────────────────────────────────────────────────────────────────────
  // The dashboard / documentation-card mechanic adds cognitive load that
  // first-time players (the classroom audience) bounce off. We collapse
  // the gameplay loop to its core: click your territory → click an enemy
  // → answer the quiz. To get there without ripping out the reducer
  // (which the server still runs and which 117 tests cover), we auto-
  // progress through the non-combat phases on the client when it's the
  // player's turn.
  //
  //   - draft-plan       → END_PHASE immediately
  //   - document-deploy  → END_PHASE immediately
  //   - incident-fortify → END_TURN immediately
  //   - execute-tests    → player drives, no auto-action
  //
  // We deliberately DO NOT call DEPLOY_CUBES here. Two reducer-level
  // gates make it unreachable in simplified play:
  //   (a) `reducer.ts` rejects DEPLOY_CUBES outside `document-deploy`
  //       with `wrong_phase`.
  //   (b) `getMaxDeployableCubes` in `@qd/rules/dashboard.ts` short-
  //       circuits to 0 unless the player has played the Test Policy
  //       documentation card — which the simplified UI never exposes,
  //       so it's permanently false.
  // Together (a) and (b) mean a deploy attempt is always rejected by
  // the server. Because gameStore actions are fire-and-forget WS
  // messages with no error surface, a rejected DEPLOY_CUBES would let
  // the auto-pilot's `return` strand the client on draft-plan with
  // reserves > 0 forever — and every other client would hang on
  // "waiting for {Name}". So: never call deployCubes here. Reserves
  // accumulate harmlessly until simplified play grows an auto-Policy
  // mechanic.
  useEffect(() => {
    if (!gameState || !lobbySelfId || !myTurn) return;
    // Combat-mid-question: the modal's CombatTimerArc owns the flow and
    // dispatches TURN_TIMEOUT on expiry. Skip the auto-pilot.
    if (attackState.kind === 'combatActive') return;
    // Combat-resolved / territory-conquered: the player normally clicks
    // Continue/End Combat or the cube-mover. If the per-turn timer has
    // expired on these states, the modal can't dispatch (its
    // handleTimerExpire is gated on combatActive), so we end combat
    // here. END_COMBAT clears attackState back to idle; the next render
    // takes the execute-tests branch below and force-advances the turn.
    if (
      attackState.kind === 'combatResolved' ||
      attackState.kind === 'territoryConquered'
    ) {
      if (turnTimer.enabled && turnTimer.isExpired) {
        endCombat();
      }
      return;
    }
    // attackState.kind === 'idle' from here on.

    const phase = gameState.phase;
    if (phase === 'execute-tests') {
      // Player drives execute-tests by clicking territories. The only
      // auto-action here is the per-turn timer: when it expires on the
      // current player's client, fast-forward to incident-fortify so the
      // turn doesn't stall on an unresponsive player.
      // Guard: only fire once — isExpired stays true until the server
      // broadcasts the next turn, so without this every render would
      // dispatch END_PHASE repeatedly.
      if (turnTimer.enabled && turnTimer.isExpired && !timerEndPhaseFiredRef.current) {
        timerEndPhaseFiredRef.current = true;
        endPhase();
      }
      return;
    }
    if (phase === 'incident-fortify') {
      endTurn();
      return;
    }
    // draft-plan or document-deploy → just advance.
    endPhase();
  }, [
    gameState,
    lobbySelfId,
    myTurn,
    attackState.kind,
    turnTimer.enabled,
    turnTimer.isExpired,
    endPhase,
    endTurn,
    endCombat,
  ]);

  if (!gameState) {
    return (
      <SlateBackground>
        <div
          style={{
            minHeight: '100dvh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: surfaces.chalk,
            fontFamily: fontStacks.body,
            fontSize: 14,
            opacity: 0.7,
          }}
        >
          Syncing match state…
        </div>
      </SlateBackground>
    );
  }

  return (
    <DndContext onDragEnd={() => undefined}>
      <SlateBackground>
        {isMobile ? (
          <MobilePlayLayout
            gameState={gameState}
            selfId={lobbySelfId}
            currentPlayer={currentPlayer}
            attackState={attackState}
            sourceName={sourceName}
            targetName={targetName}
            playerCount={playerCount}
            sessionId={routeSessionId}
            isMyTurn={myTurn}
            turnTimerSeconds={turnTimerSeconds}
            onSelectSource={(id: TerritoryId) => selectSource(id)}
            onSelectTarget={(id: TerritoryId) => selectTarget(id)}
            onCommitAttack={commitAttack}
            onEndCombat={endCombat}
            onEndPhase={endPhase}
            inRescueDeployMode={inRescueDeployMode}
            isHardStuck={isHardStuck}
            reserveCubes={selfPlayer?.reserveCubes ?? 0}
            onRescueDeploy={onRescueDeploy}
          />
        ) : (
          <>
            <StatusBar
              roundNumber={gameState.roundNumber}
              turnNumber={gameState.turnNumber}
              currentPlayerIdx={gameState.currentPlayerIdx}
              currentPlayer={currentPlayer}
              selfPlayer={selfPlayer}
              isMyTurn={myTurn}
              sessionId={routeSessionId}
              turnTimerSeconds={turnTimerSeconds}
            />

            <PhaseCoach
              isMyTurn={myTurn}
              currentPlayerName={currentPlayer?.name ?? null}
              phase={gameState.phase}
              attackState={attackState}
              sourceName={sourceName}
              targetName={targetName}
              dashboard={dashboard}
              reserveCubes={selfPlayer?.reserveCubes ?? 0}
              inRescueDeployMode={inRescueDeployMode}
              isHardStuck={isHardStuck}
            />

            <div
              style={{
                position: 'absolute',
                top: 56 + PHASE_COACH_HEIGHT,
                left: 0,
                right: 0,
                bottom: 80, // just enough room for PhaseControls
                // Hide the map while the combat modal is up. The 94%-
                // opaque overlay covers the map anyway, but the browser
                // still computes layout / paint / SVG filters for the
                // entire territory tree (60 paths + clipPaths +
                // feTurbulence) every frame, which tanks FPS on weaker
                // hardware. `display: none` skips the whole subtree.
                display: combatModalIsOpen(attackState) ? 'none' : 'flex',
                flexDirection: 'column',
              }}
            >
              <MapView
                playerCount={playerCount}
                gameState={gameState}
                selfId={lobbySelfId}
                onSelectSource={(id: TerritoryId) => selectSource(id)}
                onSelectTarget={(id: TerritoryId) => selectTarget(id)}
                onEndCombat={endCombat}
                mode={inRescueDeployMode ? 'deploy' : 'attack'}
                onDeploy={onRescueDeploy}
              />
            </div>
            {/*
              Project Dashboard + CardHand removed for the simplified
              gameplay loop. The educational content lives in the quiz
              questions that pop up during combat (Tier 1/2/3 ISO 29119-3
              questions sourced by territory risk level). The dashboard
              chain mechanic is preserved in @qd/rules and can be re-
              surfaced behind a "deep mode" toggle later.
            */}

            <PhaseControls
              gameState={gameState}
              isMyTurn={myTurn}
              onCommitAttack={commitAttack}
              onEndPhase={endPhase}
              onEndTurn={endTurn}
              onEndCombat={endCombat}
            />
            
            <PlayerRoster gameState={gameState} selfId={lobbySelfId} />
          </>
        )}

        <ChalkQuizModal
          attackState={attackState}
          sourceName={sourceName}
          targetName={targetName}
          // Surface the configured per-turn timer to the modal. A
          // combatant's expiry triggers TURN_TIMEOUT (force-end turn);
          // bystanders see a subdued visual countdown for urgency.
          turnTimer={turnTimerSeconds}
          onSubmit={answerQuestion}
          onSubmitLearning={handleLearningSubmit}
          onTurnTimeout={handleTurnTimeout}
          // No Continue / End Combat buttons in the buzzer-race
          // redesign — the modal auto-dismisses combatResolved after a
          // brief reveal by dispatching END_COMBAT here, which
          // transitions attackState back to idle. If BR-Backend ends up
          // auto-transitioning combatResolved → idle server-side, this
          // callback is a no-op (because the modal never sees
          // combatResolved long enough to schedule the timeout).
          onCombatResolvedDismiss={endCombat}
          onMoveCubes={moveCubesAfterConquest}
          isAttacker={myTurn}
          isDefender={isDefender}
          attackerLockedOut={attackerLockedOut}
          defenderLockedOut={defenderLockedOut}
          attackerName={currentPlayer?.name ?? 'The attacker'}
          localLearningAnswer={localLearningAnswer}
          // ISO 29119-3 clause-citation paragraph for the just-resolved
          // question. Forwarded to all clients (combatants AND
          // bystanders) so every player learns from each combat. Empty
          // string for any AttackState other than `combatResolved`.
          explanation={
            attackState.kind === 'combatResolved' ? attackState.explanation : ''
          }
        />

        <PhaseTransitionToast />

        {gameState.outerPhase === 'ended' ? (
          <GameOverOverlay gameState={gameState} onReturnHome={() => navigate('/')} />
        ) : (
          <HowToPlayModal />
        )}
      </SlateBackground>
    </DndContext>
  );
}

// -----------------------------------------------------------------------------
// PhaseControls — phase-specific bottom-bar action buttons
// -----------------------------------------------------------------------------

interface PhaseControlsProps {
  readonly gameState: GameState;
  readonly isMyTurn: boolean;
  readonly onCommitAttack: (cubes: 1 | 2 | 3) => void;
  readonly onEndPhase: () => void;
  readonly onEndTurn: () => void;
  readonly onEndCombat: () => void;
}

function PhaseControls({
  gameState,
  isMyTurn,
  onCommitAttack,
  onEndPhase,
  onEndTurn,
  onEndCombat,
}: PhaseControlsProps): ReactNode {
  // Combat-active or conquered: the modal handles the controls.
  const ak = gameState.attackState.kind;
  const modalOwnsFlow =
    ak === 'combatActive' || ak === 'combatResolved' || ak === 'territoryConquered';
  if (modalOwnsFlow) return null;

  // Identify the current player so we can show "Waiting for X" when it's
  // not our turn — instead of an empty screen.
  const currentPlayerId = gameState.playerOrder[gameState.currentPlayerIdx];
  const currentPlayerName =
    gameState.players.find((p) => p.id === currentPlayerId)?.name ?? 'opponent';
  const phaseLabel = PHASE_LABEL[gameState.phase];

  // Primary action label for the current state.
  let primaryLabel = 'End Turn';
  let primaryAction: () => void = onEndTurn;
  let primaryEnabled = isMyTurn;
  if (gameState.phase !== 'incident-fortify') {
    primaryLabel = 'End Phase';
    primaryAction = onEndPhase;
  }

  return (
    <div
      role="region"
      aria-label="Turn controls"
      style={{
        position: 'fixed',
        // Left margin of 260px clears the TitleCartouche (bottom-left ~220px wide).
        // Right margin of 16px is enough since the PlayerRoster is fixed/right.
        left: 260,
        right: 16,
        bottom: 16,
        zIndex: 7,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        pointerEvents: 'none', // children opt back in
      }}
    >
      <TurnBadge
        isMyTurn={isMyTurn}
        currentPlayerName={currentPlayerName}
        phaseLabel={phaseLabel}
      />
      <div style={{ display: 'flex', gap: 10, pointerEvents: 'auto' }}>
        {ak === 'targetSelected' ? (
          <CommitAttackButtons
            maxCubes={maxCommittableCubes(gameState)}
            onCommit={onCommitAttack}
            onCancel={onEndCombat}
          />
        ) : (
          <ChalkButton
            variant="primary"
            disabled={!primaryEnabled}
            onClick={primaryAction}
            ariaLabel={primaryLabel}
          >
            {primaryLabel}
          </ChalkButton>
        )}
      </div>
    </div>
  );
}

const PHASE_LABEL: Readonly<Record<GameState['phase'], string>> = {
  'draft-plan': 'Getting Ready',
  'document-deploy': 'Setting Up',
  'execute-tests': 'Attack!',
  'incident-fortify': 'Wrapping Up',
};

interface TurnBadgeProps {
  readonly isMyTurn: boolean;
  readonly currentPlayerName: string;
  readonly phaseLabel: string;
}

function TurnBadge({ isMyTurn, currentPlayerName, phaseLabel }: TurnBadgeProps): ReactNode {
  return (
    <div
      style={{
        pointerEvents: 'auto',
        padding: '8px 14px',
        background: isMyTurn
          ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
          : 'rgba(10,20,50,0.88)',
        color: '#ffffff',
        borderRadius: 8,
        border: isMyTurn ? `1.5px solid rgba(255,255,255,0.4)` : '1.5px solid rgba(255,255,255,0.15)',
        fontFamily: fontStacks.mono,
        fontSize: 11,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        boxShadow: isMyTurn ? '0 4px 16px rgba(239,68,68,0.5)' : '0 4px 12px rgba(0,0,0,0.35)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <span
        style={{
          display: 'inline-block',
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: isMyTurn ? '#ef4444' : '#64748b',
          animation: isMyTurn ? 'qd-turn-pulse 1.6s ease-in-out infinite' : 'none',
          flexShrink: 0,
        }}
      />
      <span style={{ fontWeight: 700, color: isMyTurn ? '#fca5a5' : '#94a3b8' }}>
        {isMyTurn ? 'Your Turn' : `Waiting · ${currentPlayerName}`}
      </span>
      <span style={{ opacity: 0.55, color: '#94a3b8' }}>· {phaseLabel}</span>
      <style>{`
        @keyframes qd-turn-pulse {
          0%,100% { transform: scale(1); opacity: 1 }
          50%     { transform: scale(1.4); opacity: 0.7 }
        }
      `}</style>
    </div>
  );
}

interface CommitAttackButtonsProps {
  readonly maxCubes: 1 | 2 | 3;
  readonly onCommit: (cubes: 1 | 2 | 3) => void;
  readonly onCancel: () => void;
}

function CommitAttackButtons({
  maxCubes,
  onCommit,
  onCancel,
}: CommitAttackButtonsProps): ReactNode {
  const options: ReadonlyArray<1 | 2 | 3> = [1, 2, 3];
  return (
    <>
      <ChalkButton variant="secondary" onClick={onCancel} ariaLabel="Cancel attack">
        Cancel
      </ChalkButton>
      {options
        .filter((c) => c <= maxCubes)
        .map((c) => (
          <ChalkButton
            key={c}
            variant="primary"
            onClick={() => onCommit(c)}
            ariaLabel={`Commit ${c} cubes`}
          >
            Commit {c}
          </ChalkButton>
        ))}
    </>
  );
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function isMyTurn(gameState: GameState, myId: PlayerId | null): boolean {
  if (!myId) return false;
  return gameState.playerOrder[gameState.currentPlayerIdx] === myId;
}

/** True when the combat modal occludes the map — used to skip rendering it. */
function combatModalIsOpen(attackState: AttackState): boolean {
  return (
    attackState.kind === 'combatActive' ||
    attackState.kind === 'combatResolved' ||
    attackState.kind === 'territoryConquered'
  );
}

function maxCommittableCubes(gameState: GameState): 1 | 2 | 3 {
  const a = gameState.attackState;
  if (a.kind !== 'targetSelected') return 1;
  const src = gameState.territories[a.sourceId];
  if (!src) return 1;
  const garrison = 1;
  const available = Math.max(0, src.cubeCount - garrison);
  if (available >= 3) return 3;
  if (available === 2) return 2;
  return 1;
}

function derivePlayerCount(state: GameState | null): PlayerCount {
  // The map reveal is keyed off the player count at match start. We
  // clamp to the protocol-supported 2..5 range (5p reveals all five
  // module groups except compliance, per buildActiveMap).
  if (!state) return 2;
  const n = state.players.length;
  if (n <= 2) return 2;
  if (n === 3) return 3;
  if (n === 4) return 4;
  if (n === 5) return 5;
  return 6;
}

function buildTerritoryNameLookup(playerCount: PlayerCount): ReadonlyMap<string, string> {
  // Walk the global TERRITORIES table and let lookups tolerate inactive
  // ids (those resolve to undefined; callers fall back to the raw id).
  // The active-map filter runs in MapView; here we just need name
  // lookup, so we don't gate on `playerCount`.
  void playerCount;
  const map = new Map<string, string>();
  for (const t of TERRITORIES) {
    map.set(String(t.id), t.displayName);
  }
  return map;
}

function sourceTerritoryName(
  attack: AttackState,
  names: ReadonlyMap<string, string>,
): string {
  switch (attack.kind) {
    case 'sourceSelected':
    case 'targetSelected':
    case 'combatActive':
    case 'combatResolved':
    case 'territoryConquered':
      return names.get(attack.sourceId) ?? attack.sourceId;
    default:
      return '';
  }
}

function targetTerritoryName(
  attack: AttackState,
  names: ReadonlyMap<string, string>,
): string {
  switch (attack.kind) {
    case 'targetSelected':
    case 'combatActive':
    case 'combatResolved':
    case 'territoryConquered':
      return names.get(attack.targetId) ?? attack.targetId;
    default:
      return '';
  }
}

